import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Round } from '../api/types';

const REGRAS = [
  'Cada palpite (bolão) custa R$ 10,00.',
  'O pagamento é feito via Pix.',
  'O bolão só é confirmado após a confirmação do pagamento.',
  'Após confirmado, os palpites NÃO podem mais ser editados.',
  'Cada usuário pode comprar quantos bolões quiser por rodada.',
  'São 3 rodadas (fase de grupos, 24 jogos cada).',
  'Os 5 melhores pontuadores de cada rodada são os ganhadores.',
  'Os usuários são identificados pelo número de celular.',
];

interface PontuacaoRow {
  pts: number;
  regra: string;
  exemplo: { palpite: string; resultado: string };
}

const PONTUACAO: PontuacaoRow[] = [
  { pts: 10, regra: 'Placar exato',                                    exemplo: { palpite: '3 × 1', resultado: '3 × 1' } },
  { pts: 4,  regra: 'Empate certo, com placar diferente',              exemplo: { palpite: '2 × 2', resultado: '0 × 0' } },
  { pts: 3,  regra: 'Acerto dos gols do vencedor ',                    exemplo: { palpite: '3 × 1', resultado: '3 × 2' } },
  { pts: 2,  regra: 'Acerto gols do perdedor',                         exemplo: { palpite: '3 × 1', resultado: '2 × 1' } },
  { pts: 2,  regra: 'Apenas o vencedor correto',                       exemplo: { palpite: '3 × 1', resultado: '2 × 0' } },
  { pts: 0,  regra: 'Erro total',                                      exemplo: { palpite: '2 × 2', resultado: '3 × 1' } },
];

function posBadgeClass(pos: number): string {
  if (pos === 1) return 'bg-yellow-400 text-yellow-900';
  if (pos === 2) return 'bg-slate-300 text-slate-800';
  if (pos === 3) return 'bg-amber-600 text-white';
  return 'bg-slate-200 text-slate-700';
}

function ptsBadgeClass(pts: number): string {
  if (pts >= 10) return 'bg-brand-500 text-white';
  if (pts >= 4) return 'bg-emerald-100 text-emerald-700';
  if (pts >= 2) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-200 text-slate-600';
}

export default function RulesPage() {
  const { data: rounds } = useQuery({
    queryKey: ['rounds'],
    queryFn: async () => (await api.get<Round[]>('/rounds')).data,
  });

  const { data: paymentConfig } = useQuery({
    queryKey: ['payment-config'],
    queryFn: async () => (await api.get<{ free_rounds: number[] }>('/payment-config')).data,
  });
  const freeRound = paymentConfig?.free_rounds?.[0];

  return (
    <div className="space-y-6">
      {freeRound && (
        <section className="card space-y-3 border-2 border-brand-500 bg-brand-500/5">
          <h2 className="text-xl font-bold">🍺 Rodada {freeRound} — Especial</h2>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1.5">
            <li><strong>Aposta grátis</strong> — sem pagamento.</li>
            <li><strong>1 aposta por pessoa.</strong></li>
          </ul>
          <div>
            <h3 className="font-semibold text-sm mb-2">Premiação</h3>
            <ul className="space-y-2">
              {[1, 2].map((pos) => (
                <li
                  key={pos}
                  className="flex items-center gap-3 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0"
                >
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm ${posBadgeClass(pos)}`}
                  >
                    {pos}º
                  </span>
                  <span className="text-sm text-slate-700">
                    1 caixa de cerveja em lata (Brahma, Amstel ou Skol)
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="card space-y-3">
        <h2 className="text-xl font-bold">Regulamento</h2>
        <ol className="list-decimal pl-5 text-sm text-slate-700 space-y-1.5">
          {REGRAS.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ol>
      </section>

      <section className="card space-y-3">
        <div>
          <h2 className="text-xl font-bold">Contagem de pontos</h2>
          <p className="text-xs text-slate-500 mt-1">
            Aplicação por prioridade — a primeira regra que se aplica é a única que vale.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-2 pr-2 border-b border-slate-200">Pts</th>
                <th className="py-2 pr-2 border-b border-slate-200">Critério</th>
                <th className="py-2 pr-2 border-b border-slate-200 hidden sm:table-cell">Exemplo</th>
              </tr>
            </thead>
            <tbody>
              {PONTUACAO.map((row, i) => (
                <tr key={i} className="align-top">
                  <td className="py-2 pr-2 border-b border-slate-100">
                    <span
                      className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm ${ptsBadgeClass(row.pts)}`}
                    >
                      {row.pts}
                    </span>
                  </td>
                  <td className="py-2 pr-2 border-b border-slate-100">
                    <div className="font-medium text-slate-800">{row.regra}</div>
                    <div className="text-xs text-slate-500 mt-1 sm:hidden">
                      Ex: palpite <strong>{row.exemplo.palpite}</strong> · resultado{' '}
                      <strong>{row.exemplo.resultado}</strong>
                    </div>
                  </td>
                  <td className="py-2 pr-2 border-b border-slate-100 hidden sm:table-cell whitespace-nowrap">
                    <div className="text-xs text-slate-500">palpite</div>
                    <div className="font-mono">{row.exemplo.palpite}</div>
                    <div className="text-xs text-slate-500 mt-1">resultado</div>
                    <div className="font-mono">{row.exemplo.resultado}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {rounds && rounds.length > 0 && (
        <section className="card space-y-2">
          <h2 className="text-xl font-bold">Datas das rodadas</h2>
          <ul className="text-sm space-y-1">
            {rounds.map((r) => (
              <li key={r.id} className="flex flex-col sm:flex-row sm:justify-between border-b border-slate-100 pb-1">
                <span className="font-medium">{r.name}</span>
                <span className="text-slate-600">
                  Apostas fecham em {new Date(r.betting_closes_at).toLocaleString('pt-BR')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
